import {
  collection,
  doc,
  getDocs,
  getDoc,
  setDoc,
  updateDoc,
  query,
  where,
  orderBy,
  limit,
  addDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../database/firebase";
import { getNextBillNumber } from "./billNumberService";

/**
 * Search users by name or mobile number
 * @param {string} searchTerm - Name or mobile number to search
 * @returns {Promise<Array>} - Array of matching users
 */
export const searchUsers = async (searchTerm) => {
  try {
    const usersRef = collection(db, "users");
    const matchingUsers = [];

    // Search by name (case-insensitive partial match)
    const nameQuery = query(
      usersRef,
      where("personalDetails.name", ">=", searchTerm),
      where("personalDetails.name", "<=", searchTerm + "\uf8ff"),
      limit(10)
    );

    // Search by mobile number
    const mobileQuery = query(
      usersRef,
      where("personalDetails.mobile", "==", searchTerm),
      limit(10)
    );

    // Execute both queries
    const [nameResults, mobileResults] = await Promise.all([
      getDocs(nameQuery),
      getDocs(mobileQuery),
    ]);

    // Combine results and remove duplicates
    const userMap = new Map();

    nameResults.forEach((doc) => {
      userMap.set(doc.id, { id: doc.id, ...doc.data() });
    });

    mobileResults.forEach((doc) => {
      userMap.set(doc.id, { id: doc.id, ...doc.data() });
    });

    return Array.from(userMap.values());
  } catch (error) {
    console.error("Error searching users:", error);
    throw error;
  }
};

/**
 * Get user by ID
 * @param {string} userId - User ID
 * @returns {Promise<Object>} - User data
 */
export const getUserById = async (userId) => {
  try {
    const userDoc = await getDoc(doc(db, "users", userId));
    if (userDoc.exists()) {
      return { id: userDoc.id, ...userDoc.data() };
    }
    return null;
  } catch (error) {
    console.error("Error getting user:", error);
    throw error;
  }
};

/**
 * Add or update daily record for a user
 * @param {string} userId - User ID
 * @param {Object} recordData - Daily record data
 * @returns {Promise<void>}
 */
export const addDailyRecord = async (userId, recordData) => {
  try {
    const userRef = doc(db, "users", userId);
    const userDoc = await getDoc(userRef);

    if (!userDoc.exists()) {
      throw new Error("User not found");
    }

    const userData = userDoc.data();
    const existingRecords = userData.dailyRecords?.records || [];

    // Check if record for this date already exists
    const dateIndex = existingRecords.findIndex(
      (record) => record.date === recordData.date
    );

    let updatedRecords;
    if (dateIndex >= 0) {
      // Update existing record
      updatedRecords = [...existingRecords];
      updatedRecords[dateIndex] = { ...recordData };
    } else {
      // Add new record
      updatedRecords = [...existingRecords, recordData];
    }

    // Sort records by date (newest first)
    updatedRecords.sort((a, b) => new Date(b.date) - new Date(a.date));

    // Calculate average weight from last 7 records
    const recentRecords = updatedRecords.slice(0, 7);
    const avgWeight =
      recentRecords.reduce((sum, r) => sum + (r.weight || 0), 0) /
      recentRecords.length;

    // Update user document
    await updateDoc(userRef, {
      dailyRecords: {
        average: avgWeight.toFixed(2),
        records: updatedRecords,
      },
      updatedAt: new Date().toISOString(),
    });

    return { success: true, message: "Daily record updated successfully" };
  } catch (error) {
    console.error("Error adding daily record:", error);
    throw error;
  }
};

/**
 * Add shake top-up for a user
 * THIS FEATURE HAS BEEN REMOVED
 */
export const addShakeTopUp = async (userId, topUpData, adminId) => {
  throw new Error('Shake top-up feature has been removed');
};

/**
 * Get all shake top-ups for admin/super admin
 * THIS FEATURE HAS BEEN REMOVED
 */
export const getAllShakeTopUps = async (filters = {}) => {
  throw new Error('Shake top-up feature has been removed');
};

/**
 * Get shake top-ups for a specific user
 * THIS FEATURE HAS BEEN REMOVED
 */
export const getUserShakeTopUps = async (userId) => {
  throw new Error('Shake top-up feature has been removed');
};

/**
 * Get daily records for a user
 * @param {string} userId - User ID
 * @returns {Promise<Object>}
 */
export const getUserDailyRecords = async (userId) => {
  try {
    const userDoc = await getDoc(doc(db, "users", userId));

    if (userDoc.exists()) {
      const userData = userDoc.data();
      return userData.dailyRecords || { average: 0, records: [] };
    }

    return { average: 0, records: [] };
  } catch (error) {
    console.error("Error getting daily records:", error);
    throw error;
  }
};

/**
 * Calculate shake top-up statistics
 * THIS FEATURE HAS BEEN REMOVED
 */
export const getShakeTopUpStats = async (userId = null) => {
  throw new Error('Shake top-up feature has been removed');
};

/**
 * Get all users
 * @returns {Promise<Array>} - Array of all users
 */
export const getAllUsers = async () => {
  try {
    const usersRef = collection(db, "users");
    const snapshot = await getDocs(usersRef);
    const users = [];

    snapshot.forEach((doc) => {
      const userData = doc.data();
      users.push({
        id: doc.id,
        ...userData,
      });
    });

    return users;
  } catch (error) {
    console.error("Error getting all users:", error);
    throw error;
  }
};

/**
 * Get associate users only
 * @returns {Promise<Array>} - Array of associate users
 */
export const getAssociateUsers = async () => {
  try {
    const usersRef = collection(db, "users");
    const q = query(usersRef, where("role", "==", "associate"));
    const snapshot = await getDocs(q);
    const associates = [];

    snapshot.forEach((doc) => {
      associates.push({ id: doc.id, ...doc.data() });
    });

    return associates;
  } catch (error) {
    console.error("Error getting associate users:", error);
    throw error;
  }
};

/**
 * Get users referred by a specific user
 * @param {string} referrerId - ID of the referrer
 * @returns {Promise<Array>} - Array of referred users
 */
export const getUsersByReferrer = async (referrerId) => {
  try {
    const usersRef = collection(db, "users");
    
    // Query for old structure (referredBy at root level)
    const q1 = query(usersRef, where("referredBy", "==", referrerId));
    
    // Query for new structure (accountSecurity.referredBy)
    const q2 = query(usersRef, where("accountSecurity.referredBy", "==", referrerId));
    
    // Execute both queries
    const [snapshot1, snapshot2] = await Promise.all([
      getDocs(q1),
      getDocs(q2)
    ]);
    
    const referredUsers = [];
    const userIds = new Set();
    
    // Combine results from both queries, avoiding duplicates
    snapshot1.forEach((doc) => {
      if (!userIds.has(doc.id)) {
        referredUsers.push({ id: doc.id, ...doc.data() });
        userIds.add(doc.id);
      }
    });
    
    snapshot2.forEach((doc) => {
      if (!userIds.has(doc.id)) {
        referredUsers.push({ id: doc.id, ...doc.data() });
        userIds.add(doc.id);
      }
    });

    return referredUsers;
  } catch (error) {
    console.error("Error getting referred users:", error);
    throw error;
  }
};

/**
 * Get users sponsored by a specific user
 * @param {string} sponsorId - ID or name of the sponsor
 * @returns {Promise<Array>} - Array of sponsored users
 */
export const getUsersBySponsor = async (sponsorId) => {
  try {
    const usersRef = collection(db, "users");
    
    // Query for old structure (sponsoredBy at root level)
    const q1 = query(usersRef, where("sponsoredBy", "==", sponsorId));
    
    // Query for new structure (accountSecurity.sponsoredBy)
    const q2 = query(usersRef, where("accountSecurity.sponsoredBy", "==", sponsorId));
    
    const [snapshot1, snapshot2] = await Promise.all([
      getDocs(q1),
      getDocs(q2)
    ]);
    
    const sponsoredUsers = [];
    const userIds = new Set();
    
    snapshot1.forEach((doc) => {
      if (!userIds.has(doc.id)) {
        sponsoredUsers.push({ id: doc.id, ...doc.data() });
        userIds.add(doc.id);
      }
    });
    
    snapshot2.forEach((doc) => {
      if (!userIds.has(doc.id)) {
        sponsoredUsers.push({ id: doc.id, ...doc.data() });
        userIds.add(doc.id);
      }
    });

    return sponsoredUsers;
  } catch (error) {
    console.error("Error getting sponsored users:", error);
    throw error;
  }
};

export default {
  searchUsers,
  getUserById,
  addDailyRecord,
  addShakeTopUp,
  getUserDailyRecords,
  getAllUsers,
  getAssociateUsers,
  getUsersByReferrer,
  getUsersBySponsor,
};
